import { Availability } from '@c/dashboard/pages/ListingsPage';
import { db, functions, httpsCallable } from '@util/firebase';
import { DocumentSnapshot } from 'firebase/firestore';
import { searchClient } from '@util/getAlgolia';
import { BaseGetProductsArgs } from '@util/getPropsFromConext';
import { nonNullable } from '@util/index';
import { ReasonTypes } from '@util/maps/report-reasons';
import { shouldExcludeCurrentUserFromAnalytics } from '@util/searchAnalyticsExclusion';
import { logError } from '@util/logError';
import {
  ProductDocument,
  productDocumentSchema,
} from '@util/types/firestore/products';
import { UserDocument } from '@util/types/firestore/users';
import { Pagination } from '@util/types/paginator';
import {
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { CuratedListDocument } from './products.types';

import { GetProductArgs } from './products.types';
import { CategoryDocument } from '../categories';
export const DEFAULT_LIMIT = 10;

/**
 * Escapes special characters in Algolia filter values.
 * Single quotes must be escaped with backslash to prevent filter syntax errors.
 * Example: "Owen's Originals" -> "Owen\'s Originals"
 */
function escapeAlgoliaFilter(value: string): string {
  return value.replace(/'/g, "\\'");
}

export const productsRef = collection(
  db,
  'products'
) as CollectionReference<ProductDocument>;

export const listsRef = collection(
  db,
  'lists'
) as CollectionReference<CuratedListDocument>;

export const usersRef = collection(
  db,
  'users'
) as CollectionReference<UserDocument>;

const defaultPagination: Pagination = {
  page: 1,
  totalPages: 1,
  itemsPerPage: 24,
  totalResults: 0,
};

export const getProductsByIdQuery = (ids: string[]) =>
  query(productsRef, where('id', 'in', [...ids]));

export type ApiResponsePaginated<T> = {
  results: T[];
  brands?: string[];
  pageInfo: Pagination;
};
export type ApiResponse<T> = {
  results: T;
};

export async function getProductBySlug(
  args: GetProductArgs
): Promise<ApiResponse<ProductDocument | null>> {
  const q = query(productsRef, where('slug', '==', args.slug), limit(1));
  const snapshot = await getDocs(q);
  const data = snapshot.docs?.[0]?.data() ?? null;
  return { results: data };
}
export async function getProductById(id: string) {
  const d = doc(productsRef, id);
  const snapshot = await getDoc(d);
  return snapshot.data() ?? null;
}

export async function getProductSlugs(): Promise<ApiResponse<string[]>> {
  const docsResponse = await getDocs(productsRef);
  const slugs = docsResponse.docs.map((doc) => doc.get('slug')).filter(Boolean);
  return {
    results: slugs,
  };
}

export async function getMostRecentProductSlugs(): Promise<
  ApiResponse<string[]>
> {
  const q = query(productsRef, orderBy('date_added', 'desc'), limit(1000));
  const docsResponse = await getDocs(q);
  const slugs = docsResponse.docs.map((doc) => doc.get('slug')).filter(Boolean);
  return {
    results: slugs,
  };
}

export async function getProductsByIds(
  ids: string[]
): Promise<ProductDocument[]> {
  if (ids.length === 0) return [];
  const q = query(productsRef, where('id', 'in', ids));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => d.data());
}

export async function getProducts(
  args: BaseGetProductsArgs,
  category?: CategoryDocument | null
): Promise<ApiResponsePaginated<ProductDocument>> {
  if (argsRequireAlgolia(args)) {
    return getProductsFromAlgolia(args, category);
  }

  const resultLimit = args.limit ?? DEFAULT_LIMIT;

  const constraintsUnlimited = [
    where('out_of_stock', '==', false),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    where('is_verified', '==', true),

    args.deals.includes('On Sale') ? where('on_sale', '==', true) : null,
    args.deals.includes('Featured Listings')
      ? where('is_featured', '==', true)
      : null,
    args.deals.includes('Auctions') ? where('is_auction', '==', true) : null,
    //args.deals.includes("Free Shipping") ? where('shipping', '==', 0) : null,
    args.deals.includes('Offers Enabled')
      ? where('offers_enabled', '==', true)
      : null,
    args.deals.includes('Deals and Steals')
      ? where('curated_lists', 'array-contains', 'deals-and-steals')
      : null,

    args.master ? where('gearMaster', 'array-contains', args.master) : null,
    args.seller_id ? where('seller_id', '==', args.seller_id) : null,
    args.curated_list && typeof args.curated_list === 'string'
      ? where('curated_lists', 'array-contains', args.curated_list)
      : null,

    getCategoryConstraint(
      category ? category.name : null,
      args.categoryFilters ? args.categoryFilters.name : null
    ),
    getMinPriceConstraint(args.minPrice),
    getMaxPriceConstraint(args.maxPrice),
    getSortConstraint(args.sort),
    getBrandConstraint(args.brandFilters),
    getConditionsConstraint(args.conditionFilters),
  ].filter(nonNullable);

  const constraintsLimited = [...constraintsUnlimited, limit(resultLimit)];

  const qLimited = query(productsRef, ...constraintsLimited);
  const qUnlimited = query(productsRef, ...constraintsUnlimited);

  const getDocsPromise = getDocs(qLimited);
  const getTotalResultsPromise = getCountFromServer(qUnlimited);

  const [getDocsSnapshot, totalResultsSnapshot] = await Promise.all([
    getDocsPromise,
    getTotalResultsPromise,
  ]);
  // Exclude fraud-flagged listings from all Firestore-backed product grids (/m/, seller pages,
  // deals, curated lists). Filtered in application code rather than a Firestore `where` clause:
  // ~7% of live products have `is_fraudulent` ABSENT rather than explicitly false, and both
  // `== false` and `!= true` Firestore constraints drop docs missing the field — which would hide
  // legitimate listings. `!== true` keeps false/undefined and removes only explicit fraud. (The
  // Algolia path already excludes fraud via the not-live index sync in onProductWrite.)
  const docsResponse = getDocsSnapshot.docs
    .map((x) => x.data())
    .filter((p) => p.is_fraudulent !== true);
  const totalResultsResponse = totalResultsSnapshot.data().count;

  const totalPages = Math.ceil(totalResultsResponse / args.limit) || 1;
  const currentPage = args.page > totalPages ? totalPages : args.page;

  return {
    results: docsResponse,
    brands: getBrandsFromResult(docsResponse),
    pageInfo: {
      ...defaultPagination,
      totalResults: totalResultsResponse,
      page: currentPage,
      totalPages,
    },
  };
}

export const getAllProductsPaginated = async (args: {
  lastKey?: string;
  productStatus: string;
}) => {
  const { lastKey, productStatus } = args;

  const limitValue = 10;
  const lastDoc = lastKey ? await getDoc(doc(productsRef, lastKey)) : null;

  const filterConstraints = [
    ...(productStatus !== 'is_verified'
      ? [where(productStatus, '==', true)]
      : [where(productStatus, '==', false)]),
  ];

  const getAll = (lastDoc: DocumentSnapshot<ProductDocument> | null) =>
    query(
      productsRef,
      orderBy('created', 'desc'),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      ...filterConstraints,
      limit(limitValue)
    );

  const q = getAll(lastDoc);
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map((doc) =>
    productDocumentSchema.parse(doc.data())
  );
  return { results: data };
};

export async function getAllUnverified(
  arg: string
): Promise<{ products: ProductDocument[] }> {
  const q = query(
    collection(db, 'products') as CollectionReference<ProductDocument>,
    where(arg, '==', false)
  );
  const snapshot = await getDocs(q);

  const products = snapshot.docs.map((doc) => doc.data());

  return { products };
}

export async function getProductsFromAlgolia(
  args: BaseGetProductsArgs,
  category?: CategoryDocument | null
) {
  let filters = '';

  // Category filter
  if (args.categoryFilters)
    filters += `categories:'${escapeAlgoliaFilter(args.categoryFilters.name)}' AND `;
  else if (category) filters += `categories:'${escapeAlgoliaFilter(category.name)}' AND `;

  // Seller
  if (args.seller_id) filters += `seller_id:'${args.seller_id}' AND `;

  // Curated list (supports single string or array for OR queries)
  if (args.curated_list) {
    if (Array.isArray(args.curated_list)) {
      const curatedClause = args.curated_list
        .map((list) => `curated_lists:'${escapeAlgoliaFilter(list)}'`)
        .join(' OR ');
      filters += `(${curatedClause}) AND `;
    } else {
      filters += `curated_lists:'${escapeAlgoliaFilter(args.curated_list)}' AND `;
    }
  }

  // Price range
  if (args.minPrice && args.maxPrice)
    filters += `price:${args.minPrice} TO ${args.maxPrice} AND `;
  else if (args.minPrice) filters += `price>=${args.minPrice} AND `;
  else if (args.maxPrice) filters += `price<=${args.maxPrice} AND `;

  // Brand filters (ensure array - URL params can be string for single value)
  const brandFiltersArray = Array.isArray(args.brandFilters) ? args.brandFilters : (args.brandFilters ? [args.brandFilters] : []);
  if (brandFiltersArray.length > 0) {
    const brandClause = brandFiltersArray
      .map((b) => `brand:'${escapeAlgoliaFilter(b)}'`)
      .join(' OR ');
    filters += `${brandClause} AND `;
  }

  // Condition filters (ensure array - URL params can be string for single value)
  const conditionFiltersArray = Array.isArray(args.conditionFilters) ? args.conditionFilters : (args.conditionFilters ? [args.conditionFilters] : []);
  if (conditionFiltersArray.length > 0) {
    const conditions = conditionFiltersArray
      .map((c) => `condition:'${escapeAlgoliaFilter(c)}'`)
      .join(' OR ');
    filters += `${conditions} AND `;
  }

  // UseCases Filters
  if (args.useCases.length > 0) {
    const useCaseFilters = args.useCases
      .map((c) => `filters.useCase:'${escapeAlgoliaFilter(c)}'`)
      .join(' OR ');
    filters += `${useCaseFilters} AND `;
  }

  // ✅ Deal filters (translated from Firestore where clauses)
  if (args.deals?.includes('On Sale')) filters += `on_sale:true AND `;
  if (args.deals?.includes('Featured Listings'))
    filters += `is_featured:true AND `;
  if (args.deals?.includes('Auctions')) filters += `is_auction:true AND `;
  // if (args.deals?.includes("Free Shipping")) filters += `shipping:0 AND `; // Uncomment if `shipping` is indexed
  if (args.deals?.includes('Offers Enabled'))
    filters += `offers_enabled:true AND `;
  if (args.deals?.includes('Deals and Steals'))
    filters += `curated_lists:'deals-and-steals' AND `;

  // Additional filters (e.g., filters.mount.lens, filters.sensor.type, etc.)
  if (
    args.additionalFilters &&
    Object.keys(args.additionalFilters).length > 0
  ) {
    Object.entries(args.additionalFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        const clause = values
          .map((val) => `filters.${key}:'${escapeAlgoliaFilter(val)}'`)
          .join(' OR ');
        filters += `${clause} AND `;
      }
    });
  }

  // Defaults
  filters +=
    'is_draft:false AND out_of_stock:false AND is_expired:false AND is_verified:true';

  if (filters.endsWith('AND ')) filters = filters.slice(0, -5);

  const preIndex =
    process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production'
      ? 'listings'
      : 'products';
  const index =
    args.sort === 'priceAsc'
      ? `${preIndex}_low_price`
      : args.sort === 'priceDesc'
      ? `${preIndex}_high_price`
      : args.sort === 'ranked'
      ? `${preIndex}_rank_score`
      : args.sort === 'newest'
      ? `${preIndex}_date_added_desc`
      : preIndex;

  const res = await searchClient
    .initIndex(index)
    .search<ProductDocument>(args.keyword ?? '', {
      ...(filters && { filters }),
      hitsPerPage: args.limit,
      page: args.page - 1,
      // Staff/house-store searches never count toward Algolia analytics
      // (GB #1392). initIndex bypasses the getAlgolia wrapper, so the
      // exclusion is applied here directly. Fails open: analytics on.
      ...(shouldExcludeCurrentUserFromAnalytics() && { analytics: false }),
    });

  return {
    results: res.hits,
    brands: getBrandsFromResult(res.hits),
    pageInfo: {
      page: args.page > res.nbPages ? res.nbPages : args.page,
      totalPages: res.nbPages,
      itemsPerPage: res.hitsPerPage,
      totalResults: res.nbHits,
    },
  };
}

export async function getNewArrivals(
  l = DEFAULT_LIMIT
): Promise<ApiResponse<ProductDocument[]>> {
  const q = query(
    productsRef,
    where('out_of_stock', '==', false),
    where('is_auction', '==', false),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    where('is_verified', '==', true),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getFeaturedProducts(l = DEFAULT_LIMIT) {
  // Fetch a larger pool to enable diversity selection + randomization
  const fetchLimit = Math.max(100, l * 8);
  const q = query(
    productsRef,
    where('is_featured', '==', true),
    where('out_of_stock', '==', false),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    where('is_verified', '==', true),
    orderBy('date_added', 'desc'),
    limit(fetchLimit)
  );
  const { docs } = await getDocs(q);
  const pool = docs.map((d) => d.data());

  // Shuffle the pool for randomization
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }

  // Smart selection: ensure brand + category diversity in the first 7 slots.
  // Target: at least 4 brands, 3 camera bodies, 2 lenses in the first 7.
  const selected: typeof pool = [];
  const usedBrands = new Set<string>();
  const isCamera = (p: ProductDocument) =>
    p.categories?.some((c: string) => /camera|body|mirrorless|dslr|cinema/i.test(c)) ?? false;
  const isLens = (p: ProductDocument) =>
    p.categories?.some((c: string) => /lens/i.test(c)) ?? false;

  let cameras = 0;
  let lenses = 0;

  // Pass 1: pick cameras from unique brands (need 3 cameras, 4+ brands)
  for (const p of pool) {
    if (selected.length >= 3) break;
    if (isCamera(p) && !usedBrands.has(p.brand)) {
      selected.push(p);
      usedBrands.add(p.brand);
      cameras++;
    }
  }

  // Pass 2: pick lenses from brands not yet used (need 2 lenses, expand brand count)
  for (const p of pool) {
    if (lenses >= 2) break;
    if (isLens(p) && !selected.includes(p)) {
      // Prefer a new brand, but accept any if needed
      if (!usedBrands.has(p.brand) || lenses < 2) {
        selected.push(p);
        usedBrands.add(p.brand);
        lenses++;
      }
    }
  }

  // Pass 3: fill remaining slots up to l, preferring new brands
  for (const p of pool) {
    if (selected.length >= l) break;
    if (!selected.includes(p)) {
      selected.push(p);
      usedBrands.add(p.brand);
    }
  }

  return { results: selected.slice(0, l) };
}

export async function getDealsAndSteals(l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('curated_lists', 'array-contains', 'deals-and-steals'),
    where('out_of_stock', '==', false),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    where('is_verified', '==', true),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getPopularActiveProductsOver400(l = DEFAULT_LIMIT) {
  // Use a simple query with existing indexes, then filter in memory
  // Fetch more than needed since we'll filter in memory
  const fetchLimit = Math.max(200, l * 2); // Fetch at least 200, or 2x requested to ensure we get enough after filtering

  const q = query(
    productsRef,
    where('is_verified', '==', true),
    orderBy('date_added', 'desc'), // Get recent products
    limit(fetchLimit)
  );

  const { docs } = await getDocs(q);
  let results = docs.map((d) => d.data());

  // Filter in memory for all the active requirements
  results = results.filter(product =>
    !product.out_of_stock &&
    !product.is_expired &&
    !product.is_draft &&
    product.price >= 400
  );

  // Sort by rank_score descending (popularity proxy)
  results.sort((a, b) => (b.rank_score || 0) - (a.rank_score || 0));

  // Return only the requested limit
  return { results: results.slice(0, l) };
}

export async function getBlackWeekProducts(l = DEFAULT_LIMIT) {
  // Combine featured, deals and steals, and popular products for Black Week
  // Fetch more than needed to ensure diversity after deduplication
  // Fetch extra items to guarantee at least 75 results after deduplication

  try {
    const [featured, deals, popular] = await Promise.all([
      getFeaturedProducts(30).catch(e => {
        console.error('[getBlackWeekProducts] getFeaturedProducts error:', e);
        return { results: [] };
      }),
      getDealsAndSteals(30).catch(e => {
        console.error('[getBlackWeekProducts] getDealsAndSteals error:', e);
        return { results: [] };
      }),
      getPopularActiveProductsOver400(150).catch(e => {
        console.error('[getBlackWeekProducts] getPopularActiveProductsOver400 error:', e);
        return { results: [] };
      })
    ]);

    console.log('[getBlackWeekProducts] Fetched:', {
      featured: featured.results.length,
      deals: deals.results.length,
      popular: popular.results.length
    });

  // Combine all products and deduplicate by SKU
  const productMap = new Map<string, ProductDocument>();

  // Add featured first (highest priority)
  featured.results.forEach(product => {
    productMap.set(product.sku, product);
  });

  // Add deals (second priority)
  deals.results.forEach(product => {
    if (!productMap.has(product.sku)) {
      productMap.set(product.sku, product);
    }
  });

  // Add popular items (third priority)
  popular.results.forEach(product => {
    if (!productMap.has(product.sku)) {
      productMap.set(product.sku, product);
    }
  });

  // Convert back to array
  const results = Array.from(productMap.values());

  // Sort by: on_sale first (Black Week priority), then by rank_score (popularity)
  results.sort((a, b) => {
    // Prioritize on_sale items
    if (a.on_sale && !b.on_sale) return -1;
    if (!a.on_sale && b.on_sale) return 1;

    // Then sort by popularity (rank_score)
    return (b.rank_score || 0) - (a.rank_score || 0);
  });

  console.log('[getBlackWeekProducts] After deduplication:', results.length);
  console.log('[getBlackWeekProducts] Returning:', Math.min(results.length, l));

  // Return requested limit
  return { results: results.slice(0, l) };
  } catch (error) {
    console.error('[getBlackWeekProducts] Fatal error:', error);
    return { results: [] };
  }
}

export async function getProductsByMaster(master: string) {
  try {
    const q = query(
      productsRef,
      where('out_of_stock', '==', false),
      where('is_expired', '==', false),
      where('is_verified', '==', true),
      where('master', '==', master),
      orderBy('date_added', 'desc')
    );

    const { docs } = await getDocs(q);
    const results = docs.map((d) => d.data());
    return { results };
  } catch (error) {
    console.error('Error fetching products by master:', error);
    throw error;
  }
}

export async function getAuctions(l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('is_auction', '==', true),
    where('out_of_stock', '==', false),
    // order by ending soonest
    orderBy('end_time', 'asc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getAllAuctionsBySellerId(seller_id: string) {
  const q = query(
    productsRef,
    where('seller_id', '==', seller_id),
    where('is_auction', '==', true),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    where('end_time', '>=', Date.now())
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getOnSale(l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('on_sale', '==', true),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getRecentlySold(l = DEFAULT_LIMIT) {
  const q = query(
    productsRef,
    where('out_of_stock', '==', true),
    orderBy('sold_date', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export async function getRelatedProducts(
  product: ProductDocument,
  l = DEFAULT_LIMIT
) {
  let mostSpecific = product.categories[product.categories.length - 1];
  const q = query(
    productsRef,
    where('categories', 'array-contains', mostSpecific),
    where('brand', '==', product.brand),
    where('out_of_stock', '==', false),
    where('is_verified', '==', true),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );

  const snap = await getDocs(q);

  const results: ProductDocument[] = [];
  snap.docs.forEach((d) => {
    if (d.id !== product.id) results.push(d.data());
  });

  if (results.length < l && product.categories.length > 1) {
    mostSpecific = product.categories[product.categories.length - 2];
    const q3 = query(
      productsRef,
      where('categories', 'array-contains', mostSpecific),
      where('brand', '==', product.brand),
      where('out_of_stock', '==', false),
      where('is_verified', '==', true),
      where('is_expired', '==', false),
      where('is_draft', '==', false),
      orderBy('date_added', 'desc'),
      limit(l)
    );
    const { docs } = await getDocs(q3);
    docs.forEach((d) => {
      if (d.id !== product.id && !results.some((r) => r.id === d.id))
        results.push(d.data());
    });
  }

  if (results.length < l) {
    const q2 = query(
      productsRef,
      where('categories', 'array-contains', mostSpecific),
      where('out_of_stock', '==', false),
      where('is_verified', '==', true),
      where('is_expired', '==', false),
      where('is_draft', '==', false),
      orderBy('date_added', 'desc'),
      limit(l)
    );
    const { docs } = await getDocs(q2);
    docs.forEach((d) => {
      if (d.id !== product.id && !results.some((r) => r.id === d.id))
        results.push(d.data());
    });
  }
  if (results.length < l && product.categories.length > 1) {
    mostSpecific = product.categories[product.categories.length - 2];
    const q3 = query(
      productsRef,
      where('categories', 'array-contains', mostSpecific),
      where('out_of_stock', '==', false),
      where('is_verified', '==', true),
      where('is_expired', '==', false),
      where('is_draft', '==', false),
      orderBy('date_added', 'desc'),
      limit(l)
    );
    const { docs } = await getDocs(q3);
    docs.forEach((d) => {
      if (d.id !== product.id && !results.some((r) => r.id === d.id))
        results.push(d.data());
    });
  }
  return { results };
}

export async function getMoreFromThisSeller(
  product: ProductDocument,
  l = DEFAULT_LIMIT
): Promise<ProductDocument[]> {
  const q = query(
    productsRef,
    where('seller_id', '==', product.seller_id),
    where('out_of_stock', '==', false),
    where('is_verified', '==', true),
    where('is_expired', '==', false),
    where('is_draft', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const snapshot = await getDocs(q);
  const products = snapshot.docs.map((doc) => doc.data()) ?? [];
  return products.filter((p) => p.id !== product.id);
}

export async function getSellerListingsPaginated(
  sellerId: string,
  isAdmin: boolean,
  lastKey?: string,
  condition: string | null = null,
  availability: Availability | null = null
) {
  if (!sellerId && !isAdmin) return Promise.resolve({ results: [] });

  const limitValue = 12;
  const lastDoc = lastKey ? await getDoc(doc(productsRef, lastKey)) : null;

  const filterConstraints = [
    ...(condition ? [where('condition', '==', condition)] : []),
    ...(availability ? [...getAvailabilityConstraint(availability)] : []),
  ];

  const baseQuery = (lastDoc: DocumentSnapshot<ProductDocument> | null) =>
    query(
      productsRef,
      orderBy('date_added', 'desc'),
      ...filterConstraints,
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(limitValue)
    );

  const q = isAdmin
    ? baseQuery(lastDoc)
    : lastDoc
    ? query(
        productsRef,
        where('seller_id', '==', sellerId),
        orderBy('date_added', 'desc'),
        ...filterConstraints,
        startAfter(lastDoc),
        limit(limitValue)
      )
    : query(
        productsRef,
        where('seller_id', '==', sellerId),
        orderBy('date_added', 'desc'),
        ...filterConstraints,
        limit(limitValue)
      );

  const snap = await getDocs(q);
  const results = snap.docs.map((d) => d.data());

  return { results };
}

/**
 * @returns { archived: true } when the API route archived the listing
 * instead of deleting it (it had sales history — see delete-draft.ts).
 * Callers show an informational message on archived:true; every other
 * outcome (fresh delete, alreadyGone, or the unchanged non-draft
 * soft-delete path) resolves { archived: false } and needs no special
 * handling.
 */
export async function deleteListing(
  productId: string,
  isDraft?: boolean
): Promise<{ archived: boolean }> {
  const docRef = doc(productsRef, productId);
  // #1467: drafts never shipped, so there's no Algolia/auction/order-history
  // state relying on the doc surviving — hard-delete it. The dashboard
  // drafts view (getSellerListingsPaginated + getAvailabilityConstraint)
  // filters on is_draft == true only, never deleted_at, so soft-deleting a
  // draft left it reappearing in that view looking undeletable.
  //
  // This can't be a client-side deleteDoc(): firestore.rules' `allow
  // update` on products lets a seller flip is_draft:true on their own SOLD
  // listing, so a client-enforced "drafts only" delete rule could be used
  // to destroy order-history evidence (Codex P1, this PR). The real guard
  // — is this LIVE doc actually a never-sold draft, cross-checked against
  // orders — has to run server-side, so this goes through an API route
  // instead of Firestore directly (gearfocus-web is moving off Cloud
  // Functions, hence a Next.js route and not a callable).
  if (isDraft) {
    const res = await fetch('/api/products/delete-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || 'Failed to delete listing.');
    }
    // alreadyGone:true (double-click / retry on an already-deleted draft)
    // is success, same as a fresh delete — neither is "archived."
    // archived:true means the route found sales history and archived the
    // doc instead of deleting it (see delete-draft.ts); callers surface an
    // informational message for that case.
    return { archived: !!data?.archived };
  }
  // api#295: explicit tombstone. Previously the only "deleted" signal was a
  // missing date_added, but #1422 stopped erasing date_added on delete (it
  // was needed to keep Algolia sync intact across a delete -> renew cycle),
  // which left auction go-live/expiry code with no reliable deleted marker.
  await updateDoc(docRef, { out_of_stock: true, deleted_at: Date.now() });
  return { archived: false };
}

export async function moveToDraft(productId: string): Promise<void> {
  const ref = doc(productsRef, productId);
  await updateDoc(ref, { is_draft: true, out_of_stock: true });
}

// we'll discuss this one
// prob use our ai guy
export async function getFreqPurchasedWith(
  categories: string[],
  l = DEFAULT_LIMIT
) {
  const q = query(
    productsRef,
    where(`category${categories.length - 1}`, '==', categories.pop()),
    where('out_of_stock', '==', false),
    orderBy('date_added', 'desc'),
    limit(l)
  );
  const { docs } = await getDocs(q);
  const results = docs.map((d) => d.data());
  return { results };
}

export function getProductId(): string {
  const newDocRef = doc(productsRef);
  const id = newDocRef.id;
  return id;
}

export async function addProductDoc(data: ProductDocument) {
  try {
    const parsedData = productDocumentSchema.parse(data);
    const docRef = doc(productsRef, parsedData.id);
    // `await` so a Firestore rejection (e.g. a security-rules denial) is caught
    // here and RE-THROWN rather than escaping as an unhandled promise. Without
    // it the create mutation's onSuccess fired on a denial and redirected —
    // fake success (GB #1512). Report to Sentry, then let the caller's error
    // path (mutation onError / try-catch) surface it to the seller.
    return await setDoc(docRef, parsedData);
  } catch (e) {
    logError(e);
    throw e;
  }
}

export async function updateProductDoc(data: ProductDocument) {
  try {
    const parsedData = productDocumentSchema.parse(data);
    const docRef = doc(productsRef, parsedData.id);
    return await updateDoc(docRef, parsedData);
  } catch (e) {
    // GB #1512: was a bare console.log that swallowed the error and resolved,
    // so the edit mutation's onSuccess redirected as if the save worked while
    // a rules denial silently dropped the seller's edit. Report + rethrow.
    logError(e);
    throw e;
  }
}

// Constraints
function getCategoryConstraint(
  parentCategory: string | null,
  categoryQueryValue: string | null
) {
  if (categoryQueryValue)
    return where('categories', 'array-contains', categoryQueryValue);
  else if (parentCategory)
    return where('categories', 'array-contains', parentCategory);
  else return null;
}

function getMinPriceConstraint(minPriceQueryValue?: number | null) {
  return !!minPriceQueryValue
    ? where('price', '>=', Number(minPriceQueryValue))
    : null;
}

function getMaxPriceConstraint(maxPriceQueryValue?: number | null) {
  return !!maxPriceQueryValue
    ? where('price', '<=', Number(maxPriceQueryValue))
    : null;
}

function getSortConstraint(sortQueryValue?: string | null) {
  return sortQueryValue === 'priceAsc'
    ? orderBy('price', 'asc')
    : sortQueryValue === 'priceDesc'
    ? orderBy('price', 'desc')
    : sortQueryValue === 'newest'
    ? orderBy('date_added', 'desc')
    : orderBy('rank_score', 'desc');
}

function getBrandConstraint(brandFilters: string[] = []) {
  const cleaned = brandFilters.filter(
    (b) => typeof b === 'string' && b !== '__all__' && !!b
  );

  if (cleaned.length > 0) {
    return where('brand', 'in', cleaned);
  }

  return null;
}

function getConditionsConstraint(conditionFilters: string[]) {
  if (conditionFilters && conditionFilters.length > 0) {
    return where('condition', 'in', conditionFilters);
  }
  return null;
}

// Helpers
function argsRequireAlgolia(args: BaseGetProductsArgs) {
  // Master product pages use Firestore (has gearMaster filter, Algolia doesn't)
  if (args.master) return false;

  return (
    args.keyword ||
    args.page >= 1 || // Use Algolia for all pages (includes seller filtering)
    ((args.minPrice || args.maxPrice) && args.sort === 'ranked') ||
    args.useCases.length > 0 ||
    (args.additionalFilters && Object.keys(args.additionalFilters).length > 0) ||
    Array.isArray(args.curated_list) // Multiple curated lists require Algolia
  );
}

export async function reportItem(
  id: string,
  reason: ReasonTypes,
  isUser: boolean
): Promise<void> {
  const col = isUser ? 'reports_users' : 'reports_products';
  const colRef = collection(db, col);
  const ref = doc(colRef, id);
  return setDoc(ref, { [reason]: increment(1) }, { merge: true });
}

export async function getOnSaleByCategory(
  category: string | undefined,
  l = DEFAULT_LIMIT
) {
  if (!category || category === undefined) return null;

  try {
    const q = query(
      productsRef,
      where('categories', 'array-contains', category),
      where('on_sale', '==', true),
      where('out_of_stock', '==', false),
      where('is_expired', '==', false),
      where('is_draft', '==', false),
      orderBy('date_added', 'desc'),
      limit(l)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((d) => d.data());
  } catch (error) {
    console.error('Error fetching on-sale products by category:', error);
    return null;
  }
}

export async function getCuratedListByCategory(
  list: string,
  category: string | undefined,
  l = DEFAULT_LIMIT
) {
  if (!category || category === undefined) return null;

  try {
    const q = query(
      productsRef,
      where('curated_lists', 'array-contains', list),
      where('out_of_stock', '==', false),
      where('is_expired', '==', false),
      orderBy('date_added', 'desc'),
      limit(l)
    );
    const { docs } = await getDocs(q);

    const filteredDocs = docs.filter((doc) =>
      doc.data().categories.includes(category)
    );
    return filteredDocs.map((d) => d.data());
  } catch (error) {
    console.error('Error fetching curated list by category:', error);
    return null;
  }
}

export async function updateFavoriteCount(
  id: string,
  isLiked = false
): Promise<void> {
  const ref = doc(productsRef, id);
  const val = isLiked ? -1 : 1;
  return updateDoc(ref, { favorite_count: increment(val) });
}

export async function updateProductByKey<T>(
  id: string,
  key: keyof ProductDocument,
  value: T
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { [key]: value });
}

export async function bumpProduct(id: string): Promise<void> {
  try {
    const ref = doc(productsRef, id);
    return await updateDoc(ref, {
      date_added: Date.now(),
      bumps: increment(1),
    });
  } catch (e) {
    console.log('[bumpProduct] Error', e);
  }
}

interface ProductStatus {
  created_at: number;
  status: string;
}

export async function updateStatus(
  id: string,
  status: ProductStatus
): Promise<void> {
  try {
    const ref = doc(productsRef, id);
    return await updateDoc(ref, { statuses: arrayUnion(status) });
  } catch (e) {
    console.log('[updateStatus] Error', e);
  }
}

export async function expireProduct(
  id: string,
  expired: boolean
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { is_expired: !expired });
}

export async function renewProduct(id: string): Promise<void> {
  const ref = doc(productsRef, id);
  // api#295 (Codex P1, PR #1434 review): renewProduct() is intentionally
  // lightweight — it only flips is_expired/date_updated and does not
  // recompute out_of_stock or auction start/end times. RenewListingModal's
  // "Renew Listing" button is reachable from ProductDetails.tsx for a
  // deleted, expired auction (the button there branches on is_expired
  // only, not out_of_stock — see ProductDetails.tsx:372), so clearing
  // deleted_at here would remove the api go-live sweep's only deletion
  // guard while leaving the doc in a resurrection-shaped state
  // (out_of_stock: true, stale past auction times). A deleted listing is
  // only un-deleted through the full edit form
  // (buildProductDocumentFromCreateListingForm in createListingHelpers.ts),
  // which recomputes stock/times/state before clearing the tombstone.
  // renewProduct deliberately leaves deleted_at untouched.
  return updateDoc(ref, {
    is_expired: false,
    date_updated: Date.now(),
  });
}

export async function verifyProduct(
  id: string,
  verify: boolean
): Promise<void> {
  const ref = doc(productsRef, id);
  return updateDoc(ref, { is_verified: !verify });
}

export async function removeProductFromFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayRemove(productId) });
}

export async function addProductToFavorites(
  uid: string,
  productId: string
): Promise<void> {
  const ref = doc(usersRef, uid);
  return updateDoc(ref, { favorites: arrayUnion(productId) });
}

export async function getCuratedLists(): Promise<CuratedListDocument[]> {
  const snapshot = await getDocs(listsRef);
  return snapshot.docs.map((d) => d.data()) ?? [];
}

export async function addProductToCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);

  // If adding to staff-picks, also set the timestamp for FIFO ordering
  if (curated_list === 'staff-picks') {
    return updateDoc(ref, {
      curated_lists: arrayUnion(curated_list),
      staff_picks_tagged_at: Date.now()
    });
  }

  return updateDoc(ref, { curated_lists: arrayUnion(curated_list) });
}

export async function removeProductFromCuratedList(
  productId: string,
  curated_list: string
): Promise<void> {
  const ref = doc(productsRef, productId);

  // If removing from staff-picks, also clear the timestamp
  if (curated_list === 'staff-picks') {
    return updateDoc(ref, {
      curated_lists: arrayRemove(curated_list),
      staff_picks_tagged_at: deleteField()
    });
  }

  return updateDoc(ref, { curated_lists: arrayRemove(curated_list) });
}

export async function setVacationMode(uid: string, on = false) {
  const { data } = await httpsCallable<
    { uid: string; on: boolean },
    { updated: number }
  >(
    functions,
    'vacationMode'
  )({ uid, on });
  return data?.updated ?? 0;
}

const getBrandsFromResult = (products: ProductDocument[]) =>
  [...new Set(products.map((p) => p.brand))].sort();

const brandsRef = collection(db, 'brands');

export const getBrandsByCategory = async (category: string) => {
  const q = query(
    brandsRef,
    where('categories', 'array-contains', category),
    orderBy('name', 'asc')
  );

  const { docs } = await getDocs(q);
  return docs.map((d) => d.data()).map((d) => d.name);
};

const getAvailabilityConstraint = (availabilityQueryValue?: Availability) => {
  if (availabilityQueryValue === 'inStock') {
    return [where('out_of_stock', '==', false)];
  }
  if (availabilityQueryValue === 'sold') {
    return [where('out_of_stock', '==', true), where('is_draft', '==', false)];
  }
  if (availabilityQueryValue === 'draft') {
    return [where('is_draft', '==', true)];
  }
  if (availabilityQueryValue === 'onSale') {
    return [where('on_sale', '==', true), where('out_of_stock', '==', false)];
  }
  return [];
};

export const MAX_META_DESCRIPTION_LENGTH = 160;
export const READ_MORE_TEXT = '...read more.';

/**
 * Check if a description contains UPP/partner notes that shouldn't be used as meta description.
 * These notes are condition details from partners like UsedPhotoPro that are meant for
 * the product page body, not for SEO meta descriptions.
 */
const isPartnerNotesContent = (text: string): boolean => {
  // Strip HTML tags first to check the actual content
  const stripped = text.replace(/<[^>]*>/g, '').trim();

  // Check for various notes patterns from UPP and other partners
  return (
    stripped.startsWith('Notes:') ||
    stripped.startsWith('Notes') ||
    stripped.toLowerCase().startsWith('notes:') ||
    // Empty or placeholder notes
    stripped === '' ||
    stripped === 'Notes:' ||
    stripped === 'Notes'
  );
};

export const formatListingMetaDescription = (
  description: string,
  descriptionHtml: string
): string => {
  let metaDescription = '';

  // Check if the description is UPP/partner notes content
  // If so, use the AI-generated description_html instead
  if (isPartnerNotesContent(description)) {
    // Use description_html (AI-generated content), fallback to title if empty
    metaDescription = descriptionHtml || description;
  } else {
    // Use the provided description
    metaDescription = description;
  }

  // Strip HTML tags and decode common entities
  metaDescription = metaDescription
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();

  // Calculate maximum allowed characters for the main content
  const maxContentLength = MAX_META_DESCRIPTION_LENGTH - READ_MORE_TEXT.length;

  // Truncate to fit the meta description limit, leaving space for "...read more."
  if (metaDescription.length > maxContentLength) {
    metaDescription = metaDescription.slice(0, maxContentLength).trim();

    // Ensure no word is cut mid-way
    const lastSpace = metaDescription.lastIndexOf(' ');
    if (lastSpace > 0) {
      metaDescription = metaDescription.slice(0, lastSpace);
    }
  }

  // Append "...read more." to the final description
  metaDescription += READ_MORE_TEXT;

  return metaDescription;
};
